import { Store } from "azsearchstore";
import { DefaultButton, PrimaryButton, Spinner, SpinnerSize, Stack, StackItem } from "office-ui-fabric-react";
import React, { useEffect, useCallback, useRef } from "react";
import { useState } from "react";
import AutoSuggest from "react-autosuggest";
import { _Styles } from "../../../content/styles/Page.styles";
import { ISearchProps } from "./ISearchProps";
import { UIControlsText } from '../../../../src/content/UIControlsText';
import { isFieldDisabled, isNullOrEmpty } from "../common/util";
import { PageStyles } from "../../../pages/pricingprogram/common/content/Page.Styles";
import { MessageTypeText } from '../../../models/MessageTypeText';
import { getRuleValidationMessage, getEntityDataFromSession } from "../../../components/shared/common/util";
import { SessionStorageConsts } from '../../../models/SessionStorageConsts';
import { AZURE_SEARCH_DIRECT_SUGGESTIONS_EVENT, AZURE_SEARCH_FORCE_UPDATE_EVENT } from '../../../services/AzureSearchUtil';

const sessionResourceData = getEntityDataFromSession<any>(SessionStorageConsts.resourceStrings)

/**
 * SearchBar Component.
 * @function component
 */
export const SearchBar: React.FunctionComponent<ISearchProps> = (props: ISearchProps) => {

    const [value, setValue] = useState("");
    const [suggestions, setSuggestions] = useState<string[]>([]);
    const [storeState, setStoreState] = useState(props.store.getState());
    const [isLoading, setIsLoading] = useState(false);
    const [authError, setAuthError] = useState<string | null>(null);
    const [recentlySelected, setRecentlySelected] = useState(false);
    const storeRef = useRef(props.store);
    const debounceTimeoutRef = useRef<NodeJS.Timeout>();
    const internalInputRef = useRef<HTMLInputElement>(null);
    const latestValueRef = useRef(value);
    
    // Use either the passed inputRef or our internal one
    const inputRef = props.inputRef || internalInputRef;
    
    // Helper function to preserve input focus
    const preserveFocus = useCallback(() => {
        // First try to use the ref directly
        if (inputRef && inputRef.current && document.activeElement !== inputRef.current) {
            // Store current selection positions
            const selectionStart = inputRef.current.selectionStart;
            const selectionEnd = inputRef.current.selectionEnd;
            
            // Focus and restore selection
            inputRef.current.focus();
            
            // Only restore selection if we have valid positions
            if (selectionStart !== null && selectionEnd !== null) {
                inputRef.current.setSelectionRange(selectionStart, selectionEnd);
            }
            return true;
        }
        
        // Fallback to querySelector if ref approach didn't work
        const input = document.querySelector('.react-autosuggest__input') as HTMLInputElement;
        if (input && document.activeElement !== input) {
            // Store current selection positions
            const selectionStart = input.selectionStart;
            const selectionEnd = input.selectionEnd;
            
            // Focus and restore selection
            input.focus();
            
            // Only restore selection if we have valid positions
            if (selectionStart !== null && selectionEnd !== null) {
                input.setSelectionRange(selectionStart, selectionEnd);
            }
            return true;
        }
        
        return false;
    }, [inputRef]);

    // Track the latest value for debounced callbacks that finish after new typing occurs
    useEffect(() => {
        latestValueRef.current = value;
    }, [value]);

    // Update store reference when props change
    useEffect(() => {
        storeRef.current = props.store;
        setStoreState(props.store.getState());
    }, [props.store]);

    // Get suggestions with error handling and Azure AD authentication support
    const getSuggestions = useCallback((state?: any): string[] => {
        try {
            const currentState = state || storeState;
            
            if (!currentState.suggestions || !currentState.suggestions.suggestions) {
                return [];
            }

            const selectField = currentState.parameters?.suggestionsParameters?.select;
            
            if (!selectField) {
                // Try common field names as fallback
                const suggestions = currentState.suggestions.suggestions;
                if (suggestions.length > 0) {
                    const firstSuggestion = suggestions[0];
                    
                    // Try to extract text from various possible fields
                    if (typeof firstSuggestion === 'string') {
                        return suggestions;
                    } else if (firstSuggestion['@search.text']) {
                        return suggestions.map((s: any) => s['@search.text']);
                    } else if (firstSuggestion.text) {
                        return suggestions.map((s: any) => s.text);
                    } else if (firstSuggestion.name) {
                        return suggestions.map((s: any) => s.name);
                    } else if (firstSuggestion.OrganizationName) {
                        return suggestions.map((s: any) => s.OrganizationName);
                    }
                }
                return [];
            }

            let suggestionResults = currentState.suggestions.suggestions.map((suggestion: any, index: number) => {
                
                // Handle different suggestion formats
                if (typeof suggestion === 'string') {
                    return suggestion;
                }
                if (suggestion && typeof suggestion === 'object') {
                    return suggestion[selectField] || 
                           suggestion['@search.text'] || 
                           suggestion.text || 
                           suggestion.name || 
                           suggestion.value || 
                           JSON.stringify(suggestion);
                }
                return String(suggestion);
            });


            // Remove empty/null suggestions
            suggestionResults = suggestionResults.filter((s: string) => s && s.trim());

            if (props.showDistinctSuggestions !== undefined && props.showDistinctSuggestions) {
                // Return distinct suggestions
                const distinctResults = [...new Map(suggestionResults.map((item: string) => [item, item])).values()] as string[];
                return distinctResults;
            } else {
                return suggestionResults;
            }
        } catch (error) {
            setAuthError('Failed to process search suggestions');
            return [];
        }
    }, [storeState, props.showDistinctSuggestions]);
    
    // Subscribe to store state changes for real-time updates
    useEffect(() => {
        const updateStoreState = () => {
            try {
                const newState = storeRef.current.getState();
                
                setStoreState(newState);
                
                // Extract suggestions from the new state
                if (newState.suggestions?.suggestions) {
                    const newSuggestions = getSuggestions(newState);
                    
                    if (newSuggestions.length > 0) {
                        setSuggestions(newSuggestions);
                    } else {
                        setSuggestions([]);
                    }
                } else {
                    setSuggestions([]);
                }
            } catch (error) {
                setAuthError('Failed to get search suggestions');
            }
        };

        // Set up store listener if available
        if (storeRef.current && typeof storeRef.current.subscribe === 'function') {
            const unsubscribe = storeRef.current.subscribe(updateStoreState);
            
            // Initial state update
            updateStoreState();
            
            return unsubscribe;
        } else {
            // Fallback: periodic state updates
            const interval = setInterval(updateStoreState, 500); // Check every 500ms
            return () => clearInterval(interval);
        }
    }, []);
    
    // Listen for direct API suggestion results from AzureSearchAuthService
    useEffect(() => {
        // Import the event constants at the component level
        const setupEventListeners = async () => {
            try {
                const handleDirectSuggestions = (event: CustomEvent) => {
                    try {
                        
                        const { suggestions: rawSuggestions, input } = event.detail;
                        
                        if (rawSuggestions && Array.isArray(rawSuggestions) && rawSuggestions.length > 0) {
                            
                            // Simulate the store state structure for getSuggestions
                            const simulatedState = {
                                suggestions: {
                                    suggestions: rawSuggestions
                                },
                                parameters: {
                                    suggestionsParameters: {
                                        select: 'OrganizationName'
                                    }
                                }
                            };
                            
                            // Extract suggestions using our normal processor
                            const extractedSuggestions = getSuggestions(simulatedState);
                            
                            if (extractedSuggestions.length > 0) {
                                setSuggestions(extractedSuggestions);
                            }
                        }
                    } catch (error) {
                    }
                };
                
                const handleForceUpdate = (event: CustomEvent) => {
                    try {
                        
                        const { suggestions: rawSuggestions, forceUpdate } = event.detail;
                        
                        if (forceUpdate && rawSuggestions && Array.isArray(rawSuggestions)) {
                            
                            // Check if store is stuck in fetching state
                            const state = storeRef.current.getState();
                            if (state.suggestions?.isFetching) {
                                
                                // Similar approach to direct suggestions
                                const simulatedState = {
                                    suggestions: {
                                        suggestions: rawSuggestions
                                    },
                                    parameters: {
                                        suggestionsParameters: {
                                            select: 'OrganizationName'
                                        }
                                    }
                                };
                                
                                const extractedSuggestions = getSuggestions(simulatedState);
                                setSuggestions(extractedSuggestions);
                                
                                // Try to unstick the store by resetting it
                                setTimeout(async () => {
                                    try {
                                        const { AzureSearchUtil } = await import('../../../services/AzureSearchUtil');
                                        AzureSearchUtil.resetStore(storeRef.current);
                                    } catch (resetError) {
                                    }
                                }, 100);
                            }
                        }
                    } catch (error) {
                    }
                };
                
                // Add event listeners for custom events
                document.addEventListener(AZURE_SEARCH_DIRECT_SUGGESTIONS_EVENT, handleDirectSuggestions as EventListener);
                document.addEventListener(AZURE_SEARCH_FORCE_UPDATE_EVENT, handleForceUpdate as EventListener);
                
                // Store cleanup function to be returned
                return () => {
                    document.removeEventListener(AZURE_SEARCH_DIRECT_SUGGESTIONS_EVENT, handleDirectSuggestions as EventListener);
                    document.removeEventListener(AZURE_SEARCH_FORCE_UPDATE_EVENT, handleForceUpdate as EventListener);
                };
            } catch (error) {
                return () => {}; // Return empty cleanup function on error
            }
        };
        
        // Set up event listeners and store cleanup function
        let cleanupFunction = () => {};
        setupEventListeners().then(cleanup => {
            if (cleanup) cleanupFunction = cleanup;
        });
        
        // Return cleanup function
        return () => cleanupFunction();
    }, [value, getSuggestions]);

    function getSuggestionValue(suggestion: any) {
        return suggestion;
    }
        
    // Enhanced search with Azure AD authentication error handling
    async function onSubmit() {
        try {
            setIsLoading(true);
            setAuthError(null);

            let formatValue = value.split(",");
            if ((props.hideSlashFormatter == undefined || !props.hideSlashFormatter) && !isNullOrEmpty(value) && formatValue.length > 0) {
                formatValue = formatValue.map((eachSearchValue) => {
                    return '"' + eachSearchValue + '"'
                });
            }

            const searchValue = formatValue.join();
            storeRef.current.setInput(searchValue);
            

            await props.onSubmit(searchValue);
        } catch (error) {
            setAuthError('Search failed. Please check your authentication.');
        } finally {
            setIsLoading(false);
        }
    }

    function onSearchKeyPress(event: any): void {
        if (event.keyCode === 13) {
            setTimeout(() => { onSubmit(); }, 200)
        }
    }

    function onClear() {
        setValue("");
        setSuggestions([]);
        setAuthError(null);
        props.onClear();
    }

    // Enhanced suggestion handling with Azure AD support
    const assignSearchValue = useCallback(async (searchValue: string): Promise<void> => {
        try {
            // Capture current selection positions
            let selectionStart = 0;
            let selectionEnd = 0;
            let isInputFocused = false;
            
            if (inputRef && inputRef.current) {
                selectionStart = inputRef.current.selectionStart || 0;
                selectionEnd = inputRef.current.selectionEnd || 0;
                isInputFocused = document.activeElement === inputRef.current;
            } else {
                const input = document.querySelector('.react-autosuggest__input') as HTMLInputElement;
                if (input) {
                    selectionStart = input.selectionStart || 0;
                    selectionEnd = input.selectionEnd || 0;
                    isInputFocused = document.activeElement === input;
                }
            }
            
            // Update the value state without re-rendering
            setValue(searchValue);
            
            // Update the store's input
            storeRef.current.setInput(searchValue);

            // Clear previous debounce
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }

            // Debounce suggestions to avoid too many API calls
            debounceTimeoutRef.current = setTimeout(async () => {
                try {
                    // Don't use state updates that might cause focus loss
                    // during typing. Only update visual loading indicator
                    // if necessary
                    const wasLoading = isLoading;
                    if (!wasLoading) {
                        setIsLoading(true);
                    }
                    setAuthError(null);

                    if (props.onSuggest !== undefined) {
                        let filterCondition = props.onSuggest(searchValue);
                        storeRef.current.updateSuggestionsParameters({
                            filter: filterCondition
                        });
                    }


                    await storeRef.current.suggest();
                } catch (error) {
                    setAuthError('Failed to get suggestions. Please check your connection.');
                } finally {
                    setIsLoading(false);
                    
                    // Ensure focus is preserved after loading state changes and updates
                    setTimeout(() => {
                        // If the user has typed more characters since this request started,
                        // skip restoring selection to avoid cursor jump
                        if (latestValueRef.current !== searchValue) {
                            return;
                        }
                        // Try to restore focus using the ref first
                        if (inputRef && inputRef.current && isInputFocused) {
                            inputRef.current.focus();
                            if (selectionStart !== null && selectionEnd !== null) {
                                inputRef.current.setSelectionRange(selectionStart, selectionEnd);
                            }
                        } else {
                            // Fallback to querySelector
                            const input = document.querySelector('.react-autosuggest__input') as HTMLInputElement;
                            if (input && isInputFocused) {
                                input.focus();
                                if (selectionStart !== null && selectionEnd !== null) {
                                    input.setSelectionRange(selectionStart, selectionEnd);
                                }
                            }
                        }
                    }, 0);
                }
            }, 300); // 300ms debounce
        } catch (error) {
            setAuthError('Failed to process search input');
        }
    }, [props.onSuggest, isLoading, preserveFocus, inputRef]);

    function onShowFacets() {
        props.onShowFacets?.();
    }

    const showSearchButton = () => {
        if (!isNullOrEmpty(props.hideSearchBtn)) {
            return props.hideSearchBtn;
        }
        else {
            return !isFieldDisabled(getRuleValidationMessage(sessionResourceData, MessageTypeText.FieldLabelText, "SearchButton", UIControlsText.SearchButton), props?.pageMode, props?.opportunityDealStatusCode)
        }
    }

    // Enhanced suggestion fetch with better error handling and async support
    const onSuggestionsFetchRequested = useCallback(({ value }: { value: string }) => {
        
        if (!storeRef.current) {
            setSuggestions([]);
            return;
        }

        // If we just selected a suggestion, don't fetch new ones immediately
        if (recentlySelected) {
            return;
        }

        const trimmedValue = value.trim();
        if (trimmedValue.length < 3) {
            setSuggestions([]);
            return;
        }

        try {
            // Flag to track if suggestions have been loaded
            let hasSuggestionsLoaded = false;
            
            // Update suggestions parameters with the search value
            if (props.onSuggest !== undefined) {
                let filterCondition = props.onSuggest(trimmedValue);
                storeRef.current.updateSuggestionsParameters({
                    filter: filterCondition
                });
            } else {
                // Fallback: update suggestions parameters without filter
                storeRef.current.updateSuggestionsParameters({
                    filter: "" // Empty filter allows all suggestions
                });
            }

            // Immediately try a direct API call through our debug utility
            // This approach is known to work reliably even with Azure AD authentication
            const tryDirectApiCall = async () => {
                try {
                    const { AzureSearchUtil } = await import('../../../services/AzureSearchUtil');
                    
                    // Try to reset any stuck state first
                    AzureSearchUtil.resetStore(storeRef.current);
                    
                    // Make direct API call
                    const results = await AzureSearchUtil.suggestDirect(storeRef.current, trimmedValue);
                    
                    if (results && results.value && results.value.length > 0) {
                        
                        // Create a simulated state for getSuggestions to use
                        const simulatedState = {
                            suggestions: {
                                suggestions: results.value
                            },
                            parameters: {
                                suggestionsParameters: {
                                    select: 'OrganizationName'
                                }
                            }
                        };
                        
                        const extractedSuggestions = getSuggestions(simulatedState);
                        if (extractedSuggestions.length > 0) {
                            setSuggestions(extractedSuggestions);
                            hasSuggestionsLoaded = true;
                            
                            // Use setTimeout to ensure we run after React's update cycle
                            setTimeout(preserveFocus, 0);
                        }
                    }
                } catch (directApiError) {
                }
            };
            
            // Start direct API call in parallel with the store approach
            tryDirectApiCall();
            
            // Also try the store's built-in suggest method
            try {
                const suggestResult = storeRef.current.suggest();
                
                // If it's a promise, handle it
                if (suggestResult instanceof Promise) {
                    suggestResult.catch(error => {
                    });
                }
            } catch (suggestError) {
            }
            
            // Add a single delayed check to see if suggestions were loaded
            setTimeout(() => {
                try {
                    // If no suggestions have been loaded yet, check the store state
                    if (!hasSuggestionsLoaded) {
                        const currentState = storeRef.current.getState();
                        
                        if (currentState.suggestions?.suggestions && currentState.suggestions.suggestions.length > 0) {
                            const currentSuggestions = getSuggestions(currentState);
                            if (currentSuggestions.length > 0) {
                                setSuggestions(currentSuggestions);
                                hasSuggestionsLoaded = true;
                            }
                        }
                        
                        // If store is stuck in fetching state, try to reset it
                        if (currentState.suggestions?.isFetching) {
                            import('../../../services/AzureSearchUtil').then(({ AzureSearchUtil }) => {
                                AzureSearchUtil.resetStore(storeRef.current);
                            });
                        }
                    }
                } catch (delayError) {
                }
            }, 1000);
            
        } catch (error) {
            setSuggestions([]);
            setAuthError('Failed to load suggestions');
        }
    }, [getSuggestions, props.onSuggest, suggestions.length, recentlySelected]);

    // Cleanup debounce on unmount
    useEffect(() => {
        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, []);

    return (
        <div className={[_Styles.SearchBarContainer, props.overrideCss].join(" ")}>
            {/* Show authentication error if present */}
            {authError && (
                <div style={{ color: 'red', fontSize: '12px', marginBottom: '5px' }}>
                    {authError}
                </div>
            )}
            
            <AutoSuggest
                suggestions={suggestions}
                onSuggestionsClearRequested={() => {
                    setSuggestions([]);
                }}
                onSuggestionsFetchRequested={onSuggestionsFetchRequested}
                onSuggestionSelected={(_, { suggestionValue }) => {
                    setValue(suggestionValue);
                    // Clear suggestions after selection to prevent dropdown from appearing again
                    setSuggestions([]);
                    // Set flag to prevent suggestions from appearing immediately after selection
                    setRecentlySelected(true);
                    // Reset flag after a delay
                    setTimeout(() => setRecentlySelected(false), 500);
                }}
                getSuggestionValue={getSuggestionValue}
                renderSuggestion={(suggestion) => {
                    return <span>{suggestion}</span>;
                }}
                shouldRenderSuggestions={(value) => {
                    // Only render suggestions if:
                    // 1. Input is at least 3 characters
                    // 2. We have suggestions to show
                    // 3. We haven't recently selected a suggestion
                    const shouldRender = value.trim().length >= 3 && 
                                        suggestions.length > 0 &&
                                        !recentlySelected;
                                        
                    return shouldRender;
                }}
                theme={{
                    container: 'react-autosuggest__container',
                    containerOpen: 'react-autosuggest__container--open',
                    input: 'react-autosuggest__input',
                    inputOpen: 'react-autosuggest__input--open',
                    inputFocused: 'react-autosuggest__input--focused',
                    suggestionsContainer: 'react-autosuggest__suggestions-container',
                    suggestionsContainerOpen: 'react-autosuggest__suggestions-container--open',
                    suggestionsList: 'react-autosuggest__suggestions-list',
                    suggestion: 'react-autosuggest__suggestion',
                    suggestionFirst: 'react-autosuggest__suggestion--first',
                    suggestionHighlighted: 'react-autosuggest__suggestion--highlighted',
                    sectionContainer: 'react-autosuggest__section-container',
                    sectionContainerFirst: 'react-autosuggest__section-container--first',
                    sectionTitle: 'react-autosuggest__section-title'
                }}
                inputProps={{
                    placeholder: props.placeHolderText,
                    value: value,
                    minLength: 3,
                    // Use the ref from props or internal ref for focus management
                    ref: (input) => {
                        // Store the reference to the input element
                        if (input) {
                            if (inputRef && typeof inputRef === 'object' && 'current' in inputRef) {
                                // @ts-ignore - We're ensuring inputRef has a current property
                                inputRef.current = input;
                            }
                        }
                    },
                    // Prevent blur during suggestion selection
                    onBlur: (e) => {
                        // If we're selecting a suggestion, prevent the default blur
                        // This helps maintain focus during suggestion selection
                        if (document.querySelector('.react-autosuggest__suggestion--highlighted')) {
                            e.preventDefault();
                        }
                    },
                    onChange: (_, { newValue, method }) => {
                        // If method is 'click' or 'enter', clear suggestions as it's a selection
                        if (method === 'click' || method === 'enter') {
                            setSuggestions([]);
                        }
                        assignSearchValue(newValue);
                        // Schedule focus preservation after React's updates
                        setTimeout(preserveFocus, 0);
                    },
                    onKeyUp: (e) => {
                        onSearchKeyPress(e);
                    }
                }}
                highlightFirstSuggestion={true}
                id={props.searchID}
            />
            
            <Stack horizontal>
                <StackItem>
                    <PrimaryButton 
                        text={getRuleValidationMessage(sessionResourceData, MessageTypeText.FieldLabelText, "SearchButton", UIControlsText.SearchButton)} 
                        styles={PageStyles.commandButtonPadding} 
                        onClick={onSubmit}
                        className="mt-0 me-1" 
                        disabled={showSearchButton() || isLoading}
                    />
                </StackItem>
                <StackItem>
                    <DefaultButton
                        text={props.showFacetsText} 
                        styles={PageStyles.commandButtonPadding} 
                        aria-label={getRuleValidationMessage(sessionResourceData, MessageTypeText.FieldLabelText, "ShowSearchFilter", UIControlsText.ShowSearchFilter)}
                        className="mt-0  me-1" 
                        onClick={onShowFacets} 
                        hidden={props.hideFacetsButton}
                        disabled={isLoading}
                    />
                </StackItem>
                <StackItem>
                    <DefaultButton 
                        text={getRuleValidationMessage(sessionResourceData, MessageTypeText.FieldLabelText, "Clear", UIControlsText.Clear)} 
                        styles={PageStyles.commandButtonPadding} 
                        onClick={onClear} 
                        className="mt-0  me-1 p-1" 
                        disabled={isLoading}
                    />
                </StackItem>
            </Stack>
            
            {/* Loading indicator */}
            {isLoading && (
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        fontSize: '12px',
                        color: '#666',
                        marginTop: '5px'
                    }}
                    role="status"
                    aria-live="polite"
                >
                    <Spinner size={SpinnerSize.xSmall} styles={{ root: { marginRight: 6 } }} />
                    <span>Loading suggestions...</span>
                </div>
            )}
        </div>
    );
};